import { ReactNode, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  KeyIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  MapPinIcon,
  RectangleStackIcon,
  UsersIcon,
  CloudArrowDownIcon,
  SunIcon,
  MoonIcon,
  BellIcon,
  IdentificationIcon,
  ArrowsRightLeftIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setCurrentLocation } from '../store/locationsSlice';
import { authApi } from '../services/api';
import { useDarkMode } from '../hooks/useDarkMode';
import Modal from './Modal';
import Button from './Button';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavItemWithChildren extends NavItem {
  children?: NavItem[];
  adminOnly?: boolean;
}

export default function Layout({ children }: LayoutProps) {
  const dispatch = useAppDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const location = useLocation();
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const locations = useAppSelector((state) => state.locations.locations);
  const currentLocationId = useAppSelector((state) => state.locations.currentLocationId);
  const assignments = useAppSelector((state) => state.assignments.assignments);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const isAdmin = currentUser?.role === 'admin';
  const isSiteAdmin = currentUser?.role === 'site-admin';
  const isKeyRequester = currentUser?.role === 'key-requester';
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Get the current location object
  const currentLocation = useMemo(() => {
    return locations.find(loc => loc.id === currentLocationId);
  }, [locations, currentLocationId]);

  // Determine which locations the user can access
  const accessibleLocations = useMemo(() => {
    if (isAdmin || isKeyRequester) {
      // Admin and key-requester can access all locations
      return locations;
    }
    if (isSiteAdmin && currentUser?.locationAccess) {
      // Site admin can access their assigned locations
      return locations.filter(loc => currentUser.locationAccess.includes(loc.id));
    }
    if (currentUser?.role === 'user') {
      // Regular users can only see locations where they have keys assigned
      // Get all inventory items from their active assignments
      const userActiveAssignments = assignments.filter(
        a => a.status === 'active' && a.personId === currentUser.id
      );
      const inventoryIds = new Set(userActiveAssignments.map(a => a.keyInventoryItemId));
      const locationIds = new Set<string>();
      inventory.forEach(item => {
        if (inventoryIds.has(item.id)) {
          locationIds.add(item.locationId);
        }
      });
      return locations.filter(loc => locationIds.has(loc.id));
    }
    return [];
  }, [isAdmin, isSiteAdmin, isKeyRequester, currentUser, locations, assignments, inventory]);

  // Can switch locations if user has access to multiple locations
  const canSwitchLocation = accessibleLocations.length > 1;

  const handleSwitchLocation = (locationId: string) => {
    dispatch(setCurrentLocation(locationId));
    setIsLocationModalOpen(false);
  };

  const settingsChildren: NavItem[] = [
    { name: 'Locations', href: '/settings/locations', icon: MapPinIcon },
    { name: 'Key Types', href: '/settings/keytypes', icon: RectangleStackIcon },
    { name: 'Users', href: '/settings/users', icon: UsersIcon },
    { name: 'Notifications', href: '/settings/notifications', icon: BellIcon },
    { name: 'Backup & Restore', href: '/settings/backup', icon: CloudArrowDownIcon },
  ];

  // Base navigation items (for admin and site-admin)
  const baseNavigation: NavItemWithChildren[] = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'People', href: '/people', icon: UserGroupIcon },
    { name: 'Key Inventory', href: '/inventory', icon: KeyIcon },
    { name: 'Key Requests', href: '/key-requests', icon: ClipboardDocumentListIcon },
    { name: 'Visitors', href: '/visitors', icon: IdentificationIcon },
  ];

  // Key requester navigation (limited - no visitors, different focus)
  const keyRequesterNavigation: NavItemWithChildren[] = [
    { name: 'Key Requests', href: '/key-requests', icon: ClipboardDocumentListIcon },
    { name: 'People', href: '/people', icon: UserGroupIcon },
    { name: 'Key Inventory', href: '/inventory', icon: KeyIcon },
    { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
  ];

  // Navigation based on role
  // Key requesters get limited navigation
  // Only admins can see Settings - site-admin cannot
  const navigation: NavItemWithChildren[] = isKeyRequester
    ? [
        ...keyRequesterNavigation,
        { name: 'Sign Out', href: authApi.getLogoutUrl(), icon: ArrowRightOnRectangleIcon },
      ]
    : isAdmin
    ? [
        ...baseNavigation,
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, children: settingsChildren },
        { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
        { name: 'Sign Out', href: authApi.getLogoutUrl(), icon: ArrowRightOnRectangleIcon },
      ]
    : [
        ...baseNavigation,
        { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon },
        { name: 'Sign Out', href: authApi.getLogoutUrl(), icon: ArrowRightOnRectangleIcon },
      ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isSettingsActive = location.pathname.startsWith('/settings');

  // Auto-expand settings when on a settings page
  const shouldShowSettingsExpanded = settingsExpanded || isSettingsActive;

  const getPageTitle = () => {
    // Check settings sub-pages first
    if (location.pathname.startsWith('/settings/')) {
      const child = settingsChildren.find(c => location.pathname.startsWith(c.href));
      if (child) return child.name;
    }
    const item = navigation.find((item) => isActive(item.href));
    return item?.name || 'KeyKeeper';
  };

  const renderNavItem = (item: NavItemWithChildren, mobile = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isItemActive = isActive(item.href);
    const isSignOut = item.name === 'Sign Out';

    if (hasChildren) {
      return (
        <div key={item.name}>
          <button
            onClick={() => setSettingsExpanded(!shouldShowSettingsExpanded)}
            className={`flex items-center justify-between w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
              isSettingsActive
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700'
            }`}
          >
            <div className="flex items-center">
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform ${shouldShowSettingsExpanded ? 'rotate-180' : ''}`}
            />
          </button>
          {shouldShowSettingsExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children!.map((child) => (
                <Link
                  key={child.name}
                  to={child.href}
                  onClick={() => mobile && setSidebarOpen(false)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(child.href)
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                  }`}
                >
                  <child.icon className="w-4 h-4 mr-3" />
                  {child.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (isSignOut) {
      return (
        <a
          key={item.name}
          href={item.href}
          onClick={() => mobile && setSidebarOpen(false)}
          className="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700"
        >
          <item.icon className="w-5 h-5 mr-3" />
          {item.name}
        </a>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isItemActive
            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
            : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700'
        }`}
      >
        <item.icon className="w-5 h-5 mr-3" />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 transition-colors">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-secondary-900/50 dark:bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-secondary-800 shadow-xl">
            <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">KeyKeeper</h1>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-md text-secondary-500 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            {currentLocation && (
              <div 
                className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded-lg border-2"
                style={{ 
                  borderColor: currentLocation.color || '#6B7280',
                  backgroundColor: currentLocation.color ? `${currentLocation.color}10` : undefined
                }}
              >
                <span 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: currentLocation.color || '#6B7280' }}
                />
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate">
                  {currentLocation.name}
                </span>
                {canSwitchLocation && (
                  <button
                    onClick={() => {
                      setSidebarOpen(false);
                      setIsLocationModalOpen(true);
                    }}
                    className="ml-auto p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded transition-colors"
                    title="Switch location"
                  >
                    <ArrowsRightLeftIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            </div>
            <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
              {navigation.map((item) => renderNavItem(item, true))}
            </nav>
            {currentUser && (
              <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
                <div className="text-sm mb-3">
                  <p className="font-medium text-secondary-900 dark:text-secondary-100">{currentUser.name}</p>
                  <p className="text-secondary-500 dark:text-secondary-400 capitalize">{currentUser.role.replace('-', ' ')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-secondary-800 border-r border-secondary-200 dark:border-secondary-700 transition-colors">
          <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">KeyKeeper</h1>
            {currentLocation && (
              <div 
                className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded-lg border-2"
                style={{ 
                  borderColor: currentLocation.color || '#6B7280',
                  backgroundColor: currentLocation.color ? `${currentLocation.color}10` : undefined
                }}
              >
                <span 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: currentLocation.color || '#6B7280' }}
                />
                <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300 truncate">
                  {currentLocation.name}
                </span>
                {canSwitchLocation && (
                  <button
                    onClick={() => setIsLocationModalOpen(true)}
                    className="ml-auto p-1 text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded transition-colors"
                    title="Switch location"
                  >
                    <ArrowsRightLeftIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => renderNavItem(item, false))}
          </nav>
          {currentUser && (
            <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
              <div className="text-sm mb-3">
                <p className="font-medium text-secondary-900 dark:text-secondary-100">{currentUser.name}</p>
                <p className="text-secondary-500 dark:text-secondary-400 capitalize">{currentUser.role.replace('-', ' ')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 flex items-center justify-between h-16 px-4 bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 lg:px-8 transition-colors">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 mr-4 rounded-md text-secondary-500 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 lg:hidden"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
              {getPageTitle()}
            </h2>
          </div>
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg text-secondary-500 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <SunIcon className="w-5 h-5" />
            ) : (
              <MoonIcon className="w-5 h-5" />
            )}
          </button>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>

      {/* Switch Location Modal */}
      <Modal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        title="Switch Location"
        footer={
          <Button variant="secondary" onClick={() => setIsLocationModalOpen(false)}>
            Cancel
          </Button>
        }
      >
        <div className="space-y-2">
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            Select a location to work with. All new items will be assigned to this location.
          </p>
          {accessibleLocations.map((loc) => {
            const locColor = loc.color || '#6B7280';
            return (
            <button
              key={loc.id}
              onClick={() => handleSwitchLocation(loc.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-colors text-left ${
                loc.id === currentLocationId
                  ? 'text-secondary-900 dark:text-secondary-100'
                  : 'border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700 text-secondary-900 dark:text-secondary-100'
              }`}
              style={loc.id === currentLocationId ? {
                borderColor: locColor,
                backgroundColor: `${locColor}15`
              } : undefined}
            >
              <span 
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: locColor }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{loc.name}</div>
                <div className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                  {loc.address}, {loc.city}, {loc.state} {loc.zipCode}
                </div>
              </div>
              {loc.id === currentLocationId && (
                <span 
                  className="text-xs font-medium px-2 py-1 rounded"
                  style={{ 
                    color: locColor,
                    backgroundColor: `${locColor}20`
                  }}
                >
                  Current
                </span>
              )}
            </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
